import { useMutation } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import type { HouseholdModelPortfolioModel } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import { buildUpdateClientGroupPortfolioInput } from '@ovComponents/4-module/widgets/portfolio/householdPortfolioMutationInput';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { useSnackbar } from 'hephaestus/web';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { isModelChangeProposalInFlight } from '../modelChangeProposal/modelChangeProposalErrors';
import {
  type InitializeHouseholdModelPortfolioOptions,
  InitializeHouseholdModelPortfolioVisual,
} from './initializeHouseholdModelPortfolio.visual';
import {
  SETUP_ENTITY_BOUND_MODEL,
  UPDATE_CLIENT_GROUP_PORTFOLIO,
} from './queries';
import { buildSetupEntityBoundModelInput } from './setupEntityBoundModelAdapter';

type SetupEntityBoundModelData = {
  setupEntityBoundModel?: {
    modelPortfolio?: { id?: string } | null;
    warnings?: string[] | null;
  } | null;
};

export type InitializeHouseholdModelPortfolioProps = {
  options: InitializeHouseholdModelPortfolioOptions;
  /**
   * Household ClientGroup id (the workflow completion `objectId`). It's both the
   * `boundObjectId` for `setupEntityBoundModel` and the `clientGroupId` for
   * `updateClientGroupPortfolio`. Falls back to
   * `workflowData.currentHouseholdClientGroupId`.
   */
  clientGroupId?: string;
  onNext: () => void;
  stepLoading?: boolean;
};

export const InitializeHouseholdModelPortfolio = ({
  options,
  clientGroupId,
  onNext,
  stepLoading = false,
}: InitializeHouseholdModelPortfolioProps) => {
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const { enqueueSnackbar } = useSnackbar();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { t } = useTranslation('workflowCompletions');

  const [setupEntityBoundModel, { loading: setupLoading }] =
    useMutation<SetupEntityBoundModelData>(SETUP_ENTITY_BOUND_MODEL);
  const [updateClientGroupPortfolio, { loading: attachLoading }] = useMutation(
    UPDATE_CLIENT_GROUP_PORTFOLIO,
  );

  // `modelPortfolioDetails` is shared with the generic create-model-portfolio
  // workflow (typed `any` there); it's a `HouseholdModelPortfolioModel` here
  // since that's the shape `selectHouseholdParentModelPortfolio` writes to it.
  // This step is a read-only review before the terminal mint/attach.
  const draftModel = workflowData.modelPortfolioDetails as
    | HouseholdModelPortfolioModel
    | undefined;

  // Prefer the context-written household id, fall back to the completion
  // objectId threaded from `workflowCompletion.visual`.
  const householdClientGroupId =
    workflowData.currentHouseholdClientGroupId ?? clientGroupId;

  /**
   * Terminal commit (mint-late): create the entity-bound clone of the selected
   * parent, then attach it to the household with the chosen trading process,
   * then advance. The Initialize flow has no per-section editing, so the clone
   * faithfully inherits everything from the base model — only the bind
   * (household + org) is specified. Idempotent/retry-safe: the minted clone id
   * is stored in `entityBoundModelPortfolioId`, so a failed attach re-attaches
   * without minting again (and `setupEntityBoundModel` is itself idempotent per
   * household on the backend).
   */
  const handleCommit = async () => {
    if (
      !draftModel ||
      !workflowData.baseModelPortfolioId ||
      !householdClientGroupId ||
      !workflowData.entityOrganizationId
    ) {
      // Precondition failure (nothing selected / missing ids), not a failed
      // commit — use distinct, non-"try again" copy.
      enqueueSnackbar(t('householdInitializeShells.commitMissingData'), {
        variant: 'error',
      });
      return;
    }

    try {
      let cloneId = workflowData.entityBoundModelPortfolioId;

      if (!cloneId) {
        const input = buildSetupEntityBoundModelInput({
          baseModelPortfolioId: workflowData.baseModelPortfolioId,
          boundObjectId: householdClientGroupId,
          organizationId: workflowData.entityOrganizationId,
          // Sent whether the household is being set up for the first time or
          // moved onto another model, because product-service only acts on it
          // in the one case that needs it: a live copy taken from a different
          // master is retired and a fresh one minted. A household with no copy,
          // or one already on this master, is unaffected — the copy it has is
          // reused with its customizations intact.
          replaceExistingBase: true,
        });
        const { data } = await setupEntityBoundModel({
          // The refusals this can come back with are worded for the advisor
          // below; the global handler would toast the raw message first.
          variables: { input, skipErrorHandler: true },
        });
        cloneId = data?.setupEntityBoundModel?.modelPortfolio?.id ?? undefined;
        if (!cloneId) {
          throw new Error('setupEntityBoundModel returned no model portfolio');
        }
        // Persist the minted clone id via the functional updater (merges into
        // the latest context). `setWorkflowData` already persists to the server,
        // so we must NOT follow it with `updateWorkflowContext()` — that would
        // re-persist the pre-mint context from this render's closure and drop the
        // clone id we need for attach-only retries and reloads.
        await setWorkflowData((previousData) => ({
          ...previousData,
          entityBoundModelPortfolioId: cloneId,
        }));
        if ((data?.setupEntityBoundModel?.warnings ?? []).length > 0) {
          enqueueSnackbar(t('householdInitializeShells.commitWarning'), {
            variant: 'warning',
          });
        }
      }

      await updateClientGroupPortfolio({
        variables: {
          input: buildUpdateClientGroupPortfolioInput({
            clientGroupId: householdClientGroupId,
            financialProductId: cloneId,
            tradingProcess: workflowData.tradingProcess,
          }),
        },
      });

      sendAnalytic(ovAnalyticsEvents.householdInitializeCommit);
      onNext();
    } catch (error) {
      datadogLogs.logger.error(
        'Household model portfolio initialize commit failed',
        {
          clientGroupId: householdClientGroupId,
          baseModelPortfolioId: workflowData.baseModelPortfolioId,
          entityBoundModelPortfolioId: workflowData.entityBoundModelPortfolioId,
        },
        error instanceof Error ? error : undefined,
      );
      enqueueSnackbar(
        isModelChangeProposalInFlight(error)
          ? t('householdInitializeShells.commitProposalPending')
          : t('householdInitializeShells.commitError'),
        { variant: 'error' },
      );
    }
  };

  return (
    <InitializeHouseholdModelPortfolioVisual
      options={options}
      loading={stepLoading || setupLoading || attachLoading}
      onNext={handleCommit}
      draftModel={draftModel}
      summary={{
        parentModelName:
          workflowData.baseModelSnapshot?.details?.translatedName,
        tradingProcess: workflowData.tradingProcess,
      }}
    />
  );
};
